import React from 'react';
import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  SimpleGrid,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import {
  EstadoCivil,
  IRascunhoDaAdministracao,
  RegimeCasamento,
  TipoOperacaoSocietario,
  TipoPessoaSocio,
  TipoVinculoSocio,
} from '../../../../../hooks/useSocietario';
import {
  ESTADO_CIVIL_OPTIONS,
  IWizardFormValues,
  REGIME_CASAMENTO_OPTIONS,
  TIPO_ALTERACAO_OPTIONS,
} from '../schema';

const tipoOperacaoLabels: Record<TipoOperacaoSocietario, string> = {
  constituicao: 'Constituição',
  alteracao: 'Alteração',
  transformacao: 'Transformação',
};

const tipoVinculoLabels: Record<TipoVinculoSocio, string> = {
  socio: 'Sócio',
  socio_administrador: 'Sócio-administrador',
  administrador: 'Administrador',
};

const tipoPessoaLabels: Record<TipoPessoaSocio, string> = {
  pf: 'PF',
  pj: 'PJ',
};

// Ação no quadro (alteração). Espelha AcaoSocioBlock pra o operador ver na
// revisão o que muda em cada sócio — não só o vínculo.
const acaoLabels: Record<string, { label: string; color: string }> = {
  manter: { label: 'Mantém', color: 'gray' },
  alterar: { label: 'Altera', color: 'blue' },
  sair: { label: 'Sai', color: 'red' },
  adicionar: { label: 'Novo', color: 'green' },
};

const estadoCivilLabels: Record<EstadoCivil, string> = {
  solteiro: 'Solteiro(a)',
  casado: 'Casado(a)',
  divorciado: 'Divorciado(a)',
  viuvo: 'Viúvo(a)',
  uniao_estavel: 'União estável',
  separado_judicialmente: 'Separado(a) judicialmente',
};

const regimeCasamentoLabels: Record<RegimeCasamento, string> = {
  comunhao_parcial_de_bens: 'Comunhão parcial de bens',
  comunhao_universal_de_bens: 'Comunhão universal de bens',
  separacao_total_de_bens: 'Separação total de bens',
  separacao_obrigatoria_de_bens: 'Separação obrigatória de bens',
  participacao_final_nos_aquestos: 'Participação final nos aquestos',
  estrangeiro_nao_aplicavel: 'Estrangeiro — não aplicável',
};

// Tipos de alteração marcados (step "Tipos de alteração") → label amigável. Da lista de opções:
// o mapa à mão não tinha nome fantasia, filial nem dados de sócio, e o chip saía cru.
const tipoAlteracaoLabels: Record<string, string> = Object.fromEntries(
  TIPO_ALTERACAO_OPTIONS.map(o => [o.value, o.label]),
);

const fmtBRL = (v: unknown): string => {
  const n = Number(v);
  return Number.isFinite(n)
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n)
    : '—';
};

// Número inteiro com separador de milhar (quotas). '—' quando vazio/ inválido.
const fmtNum = (v: unknown): string => {
  const n = Number(v);
  return Number.isFinite(n) && v !== null && v !== '' ? n.toLocaleString('pt-BR') : '—';
};

// Data ISO (yyyy-mm-dd) ou Date → dd/mm/aaaa. Passa reto se não reconhecer.
const fmtData = (v: unknown): string | undefined => {
  if (!v) return undefined;
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};

/** Helper: snake_case enum → label amigável (com fallback pro raw). */
const labelEstadoCivil = (v?: string | null): string | null | undefined =>
  v ? (estadoCivilLabels[v as EstadoCivil] ?? v) : v;

const labelRegimeCasamento = (v?: string | null): string | null | undefined =>
  v ? (regimeCasamentoLabels[v as RegimeCasamento] ?? v) : v;

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">
      {label}
    </Text>
    <Text fontSize="sm">{value === undefined || value === null || value === '' ? '—' : value}</Text>
  </Box>
);

// Endereço estruturado -> string única (mesmo formato inline do card Empresa).
const fmtEnd = (e: any): string | undefined =>
  String(e?.logradouro ?? '').trim()
    ? [e.logradouro, e.numero, e.complemento, e.bairro, e.cidade, e.uf, e.cep]
        .map(parte => String(parte ?? '').trim())
        .filter(Boolean)
        .join(', ')
    : undefined;

/** Nome fantasia, capital destacado e atividades da filial, como vão para a cláusula. */
const dadosDaFilial = (f: any): string[] =>
  [
    String(f?.nome_fantasia ?? '').trim() && `nome fantasia "${String(f.nome_fantasia).trim()}"`,
    Number(f?.capital_destacado) > 0 && `capital destacado de ${fmtBRL(f.capital_destacado)}`,
    String(f?.atividades ?? '').trim() && `atividades: ${String(f.atividades).trim()}`,
  ].filter(Boolean) as string[];

/** Sede nova da alteração (tipo "endereco" marcado), em texto; `undefined` fora disso. */
export const sedeNovaEmTexto = (
  v: Pick<IWizardFormValues, 'tipo_operacao' | 'tipos_alteracao' | 'endereco_estruturado'>,
): string | undefined =>
  v.tipo_operacao === 'alteracao' && (v.tipos_alteracao ?? []).includes('endereco')
    ? fmtEnd(v.endereco_estruturado)
    : undefined;

// Resumo do que muda em cada tipo marcado. Retorna dados ESTRUTURADOS (antes/
// depois/extra) pra a UI destacar a transição (não só texto cru com "→").
interface ResumoAlteracao {
  antes: string | null; // valor no vigente (null = tipo sem "antes", ex.: quadro)
  depois: string; // valor após a alteração
  extra?: string; // detalhe complementar (ex.: qtd de quotas · valor/quota)
}

const TEXTOS_PRONTOS: Record<string, string> = {
  distribuicao_de_lucros: 'Distribuição de lucros',
  regencia_supletiva: 'Regência supletiva',
};

// Outras cláusulas: o que a redação aprovada muda (títulos da Parte A, nº das cláusulas trocadas no
// vigente, quantas entram novas) e os textos prontos marcados.
const resumoDasClausulas = (aprovado?: IRascunhoDaAdministracao | null): ResumoAlteracao => {
  if (!aprovado) return { antes: null, depois: 'redação ainda não aprovada' };
  const linhas = aprovado.parte_a.map(c => c.titulo).filter(Boolean);
  (aprovado.textos_prontos ?? []).forEach(t =>
    linhas.push(`Texto pronto: ${TEXTOS_PRONTOS[t] ?? t.replace(/_/g, ' ')}`),
  );
  const trocadas = aprovado.parte_b.filter(c => c.substitui > 0).map(c => c.substitui);
  const novas = aprovado.parte_b.filter(c => c.substitui <= 0).length;
  const excluidas = aprovado.excluidas ?? [];
  const consolidacao = [
    excluidas.length &&
      `${excluidas.length > 1 ? 'saem as cláusulas' : 'sai a cláusula'} ${excluidas.join(', ')}`,
    trocadas.length &&
      `${trocadas.length > 1 ? 'cláusulas' : 'cláusula'} ${trocadas.join(', ')} do vigente`,
    novas && `${novas} cláusula${novas > 1 ? 's' : ''} nova${novas > 1 ? 's' : ''}`,
  ].filter(Boolean);
  return {
    antes: null,
    depois: linhas.join('\n') || '—',
    extra: consolidacao.length ? `na consolidação: ${consolidacao.join(' · ')}` : undefined,
  };
};

const resumoTipoAlteracao = (
  tipo: string,
  values: IWizardFormValues,
  vigente?: any,
  clausulasAprovadas?: IRascunhoDaAdministracao | null,
): ResumoAlteracao => {
  const socios = values.socios ?? [];
  switch (tipo) {
    case 'clausulas':
      return resumoDasClausulas(clausulasAprovadas);
    case 'capital':
      return {
        antes: vigente?.capital_social_atual != null ? fmtBRL(vigente.capital_social_atual) : null,
        depois: fmtBRL(values.capital_social),
        extra: `${fmtNum(values.capital_qtd_quotas)} quotas · ${fmtBRL(
          values.capital_valor_quota,
        )}/quota`,
      };
    case 'endereco':
      return {
        antes: vigente?.endereco_atual ?? null,
        depois: fmtEnd(values.endereco_estruturado) ?? values.endereco ?? '—',
      };
    case 'razao_social':
      return { antes: vigente?.razao_social ?? null, depois: values.razao_social || '—' };
    case 'nome_fantasia':
      return {
        antes: vigente?.nome_fantasia ?? null,
        depois: values.nome_fantasia || '—',
      };
    // As filiais do ato, com o que identifica cada uma: é o que vai escrito na cláusula.
    case 'abertura_filial':
      return {
        antes: null,
        depois:
          (values.filiais ?? [])
            .map(f => [fmtEnd(f), ...dadosDaFilial(f)].filter(Boolean).join(' · '))
            .filter(Boolean)
            .join('; ') || '—',
      };
    case 'extincao_filial':
      return {
        antes: null,
        depois:
          (values.filiais_fechar ?? [])
            .map(f =>
              [fmtEnd(f), f.nire && `NIRE ${f.nire}`, f.cnpj && `CNPJ ${f.cnpj}`]
                .filter(Boolean)
                .join(' · '),
            )
            .filter(Boolean)
            .join('; ') || '—',
      };
    case 'alteracao_filial': {
      const mudam = values.filiais_alterar ?? [];
      const quem = (f: (typeof mudam)[number]) =>
        [f.cnpj && `CNPJ ${f.cnpj}`, f.nire && `NIRE ${f.nire}`].filter(Boolean).join(' · ');
      return {
        antes:
          mudam
            .map(f => [quem(f), fmtEnd(f)].filter(Boolean).join(': '))
            .filter(Boolean)
            .join('; ') || null,
        depois:
          mudam
            .map(f =>
              [f.muda_endereco !== false && fmtEnd(f.novo), ...dadosDaFilial(f)]
                .filter(Boolean)
                .join(' · '),
            )
            .filter(Boolean)
            .join('; ') || '—',
      };
    }
    case 'objeto_social': {
      const nNovo = (values.cnaes ?? []).length;
      const nAntes = (vigente?.cnaes_atual ?? []).length;
      if (nAntes > 0 || nNovo > 0) {
        return { antes: `${nAntes} CNAE(s)`, depois: `${nNovo} CNAE(s)` };
      }
      return { antes: null, depois: values.objeto_social || '—' };
    }
    case 'dados_socio': {
      // Uma linha por socio que muda: "NOME: profissão → advogado; endereço → Rua ...".
      const rotulo = (lista: Array<{ value: string; label: string }>, v?: string | null) =>
        lista.find(o => o.value === v)?.label ?? v ?? '—';
      const linhas = socios
        .filter(s => s.acao === 'alterar')
        .map(s => {
          const aspectos = (s.tipos_alteracao_socio ?? []) as string[];
          const itens: string[] = [];
          if (aspectos.includes('nome')) itens.push(`nome → ${s.nome || '—'}`);
          if (aspectos.includes('profissao')) itens.push(`profissão → ${s.profissao || '—'}`);
          if (aspectos.includes('estado_civil')) {
            const regime = s.regime_casamento
              ? ` (${rotulo(REGIME_CASAMENTO_OPTIONS, s.regime_casamento)})`
              : '';
            itens.push(`estado civil → ${rotulo(ESTADO_CIVIL_OPTIONS, s.estado_civil)}${regime}`);
          }
          if (aspectos.includes('endereco')) {
            itens.push(`endereço → ${fmtEnd(s.endereco_estruturado) ?? '—'}`);
          }
          return itens.length ? `${s.nome}: ${itens.join('; ')}` : null;
        })
        .filter(Boolean) as string[];
      return { antes: null, depois: linhas.join(' | ') || 'nenhum sócio marcado para alterar' };
    }
    case 'quadro_societario': {
      const nomesAntes = (vigente?.quadro_socios_atual ?? [])
        .map((s: any) => s?.nome)
        .filter(Boolean);
      const nomesDepois = socios
        .filter(s => s.acao !== 'sair')
        .map(s => s.nome)
        .filter(Boolean);
      const entram = socios.filter(s => s.acao === 'adicionar').length;
      const saem = socios.filter(s => s.acao === 'sair').length;
      const alteram = socios.filter(s => s.acao === 'alterar').length;
      const cont: string[] = [];
      if (entram) cont.push(`${entram} entra(m)`);
      if (saem) cont.push(`${saem} sai(em)`);
      if (alteram) cont.push(`${alteram} altera(m)`);
      const compAntes = nomesAntes.join(', ');
      const compDepois = nomesDepois.join(', ');
      // DE/PARA só quando a COMPOSIÇÃO muda (entra/sai). Se só as quotas mudam
      // (mesmas pessoas), mostra o contador — o detalhe está no bloco Sócios.
      if (compAntes && compAntes !== compDepois) {
        return {
          antes: compAntes,
          depois: compDepois || '—',
          extra: cont.join(' · ') || undefined,
        };
      }
      return { antes: null, depois: cont.join(' · ') || `${socios.length} sócio(s)` };
    }
    case 'administracao': {
      const ehAdmin = (tv?: string | null) =>
        tv === 'socio_administrador' || tv === 'administrador';
      const adminAntes = (vigente?.quadro_socios_atual ?? [])
        .filter((s: any) => ehAdmin(s?.tipo_vinculo))
        .map((s: any) => s?.nome)
        .filter(Boolean)
        .join(', ');
      const adminDepois = socios
        .filter(s => s.acao !== 'sair' && ehAdmin(s.tipo_vinculo))
        .map(s => s.nome)
        .filter(Boolean)
        .join(', ');
      if (adminAntes && adminAntes !== adminDepois) {
        return { antes: adminAntes, depois: adminDepois || '—' };
      }
      return { antes: null, depois: adminDepois || '—' };
    }
    case 'outras':
      return { antes: null, depois: 'ajustes textuais livres' };
    default:
      return { antes: null, depois: '' };
  }
};

// Linha de "O que será alterado": destaca a transição De (cinza) → Para (negrito
// verde). Sem "antes" (ex.: quadro) mostra só o valor. "Antes" igual ao "depois"
// vira "mantém (sem mudança de valor)".
const LinhaAlteracao = ({ r }: { r: ResumoAlteracao }) => {
  const mudou = r.antes != null && r.antes !== r.depois;
  const semMudanca = r.antes != null && r.antes === r.depois;
  if (mudou) {
    return (
      <Box fontSize="sm">
        <Text color="gray.500">
          <Text as="span" fontSize="2xs" fontWeight="bold" color="gray.400" mr={1}>
            DE
          </Text>
          {r.antes}
        </Text>
        <Text color="green.700" fontWeight="semibold">
          <Text as="span" fontSize="2xs" fontWeight="bold" color="green.600" mr={1}>
            PARA
          </Text>
          {r.depois}
          {r.extra && (
            <Text as="span" color="gray.600" fontWeight="normal">
              {' '}
              · {r.extra}
            </Text>
          )}
        </Text>
      </Box>
    );
  }
  return (
    <Text fontSize="sm" color="gray.800" whiteSpace="pre-line">
      {r.depois}
      {semMudanca && (
        <Text as="span" color="gray.500">
          {' '}
          · mantém (sem mudança de valor)
        </Text>
      )}
      {r.extra && (
        <Text as="span" color="gray.600">
          {' '}
          · {r.extra}
        </Text>
      )}
    </Text>
  );
};

export const StepRevisao = ({
  vigente,
  clausulasAprovadas,
}: { vigente?: any; clausulasAprovadas?: IRascunhoDaAdministracao | null } = {}) => {
  const { getValues } = useFormContext<IWizardFormValues>();
  const values = getValues();
  const isAlteracao = values.tipo_operacao === 'alteracao';
  const isTransformacao = values.tipo_operacao === 'transformacao';
  // #3: em alteração, o box Empresa é referência (o foco é "O que será
  // alterado"); começa recolhido pra reduzir ruído, com toggle pra expandir.
  const empresaDisclosure = useDisclosure({ defaultIsOpen: !isAlteracao });

  // Quotas do vigente por documento (CPF/CNPJ) — pra mostrar a transição de
  // quotas por sócio (antes → depois) no bloco Sócios.
  const quotasVigentePorDoc: Record<string, number | null | undefined> = {};
  (vigente?.quadro_socios_atual ?? []).forEach((s: any) => {
    const doc = String(s?.cpf || s?.cnpj || '').replace(/\D/g, '');
    if (doc) quotasVigentePorDoc[doc] = s?.quotas;
  });

  // Revisão do quadro (2026-06-11): o total de quotas considera só quem
  // PERMANECE — o sócio que sai tem as quotas diluídas nos demais. Sem isso a
  // soma incluía o retirante e parecia não bater com o capital (162.500 vs
  // 100.000 no TIERRY).
  // Mesmo alvo da checagem do Gerar (varredura de 25/09): com capital, a quantidade do
  // capital novo; com quadro sem capital, o total do vigente; sem nenhum dos dois, não há
  // conta. Antes caía em capital ÷ R$ 1 e acusava "⚠ revise" em ato que nem mexe em quota.
  const tiposRevisao = (values.tipos_alteracao ?? []) as string[];
  const totalVigenteRevisao = (vigente?.quadro_socios_atual ?? []).reduce(
    (acc: number, s: any) => acc + (Number(s?.quotas) || 0),
    0,
  );
  let targetQuotas: number | null = null;
  if (tiposRevisao.includes('capital') && Number(values.capital_qtd_quotas) > 0) {
    targetQuotas = Number(values.capital_qtd_quotas);
  } else if (
    tiposRevisao.includes('quadro_societario') &&
    !tiposRevisao.includes('capital') &&
    totalVigenteRevisao > 0
  ) {
    targetQuotas = totalVigenteRevisao;
  }
  // "A cede N quotas a B", de quem sai e de quem continua cedendo parte (25/09).
  const cessoes = (values.socios ?? []).flatMap(s =>
    (s.acao === 'sair' || s.acao === 'alterar' ? (s.redistribuicao_quotas ?? []) : [])
      .filter(d => d && Number(d.qtd) > 0)
      .map(
        d =>
          `${s.nome || '?'} cede ${Number(d.qtd).toLocaleString('pt-BR')} quotas a ${
            (d.socio_ordem != null && values.socios[d.socio_ordem]?.nome) || '?'
          }`,
      ),
  );
  const totalPermanecentes = (values.socios ?? [])
    // Exclui quem sai (alteração) e o administrador NÃO-sócio (não detém quotas).
    .filter(s => s.acao !== 'sair' && s.tipo_vinculo !== 'administrador')
    .reduce((acc, s) => acc + (Number(s.quotas) || 0), 0);
  const somaConfere = targetQuotas != null && Math.abs(totalPermanecentes - targetQuotas) < 0.5;

  return (
    <Box>
      <Text fontSize="lg" fontWeight="bold" mb={4}>
        Revisão
      </Text>

      <Box borderWidth="1px" borderRadius="md" p={4} mb={4}>
        <Text fontWeight="bold" mb={3}>
          Operação
        </Text>
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
          <Field label="Tipo" value={tipoOperacaoLabels[values.tipo_operacao]} />
          {isAlteracao && <Field label="Contrato pai" value={values.contrato_pai_id} />}
          {isAlteracao && <Field label="Número da alteração" value={values.numero_alteracao} />}
        </SimpleGrid>
      </Box>

      {isAlteracao && (values.tipos_alteracao?.length ?? 0) > 0 && (
        <Box borderWidth="1px" borderColor="green.300" bg="green.50" borderRadius="md" p={5} mb={4}>
          <Flex align="center" gap={2} mb={1}>
            <Text fontSize="md" fontWeight="bold">
              O que será alterado
            </Text>
            <Badge colorScheme="green" borderRadius="full">
              {values.tipos_alteracao?.length ?? 0}
            </Badge>
          </Flex>
          <Text fontSize="xs" color="gray.600" mb={4}>
            Os campos não marcados permanecem iguais ao contrato vigente.
          </Text>
          <VStack align="stretch" spacing={3}>
            {(values.tipos_alteracao ?? []).map(tipo => (
              <Box key={tipo}>
                <Badge colorScheme="green" mb={1}>
                  {tipoAlteracaoLabels[tipo] ?? tipo}
                </Badge>
                <LinhaAlteracao
                  r={resumoTipoAlteracao(tipo, values, vigente, clausulasAprovadas)}
                />
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {isTransformacao && values.dados_ei && (
        <Box borderWidth="1px" borderRadius="md" p={4} mb={4}>
          <Text fontWeight="bold" mb={3}>
            Empresário Individual (origem)
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
            <Field label="Denominação" value={values.dados_ei.denominacao} />
            <Field label="Nome do titular" value={values.dados_ei.nome} />
            <Field label="CPF" value={values.dados_ei.cpf} />
            <Field
              label="RG"
              value={[values.dados_ei.rg, values.dados_ei.rg_orgao].filter(Boolean).join(' ')}
            />
            <Field label="Nacionalidade" value={values.dados_ei.nacionalidade} />
            <Field label="Estado civil" value={labelEstadoCivil(values.dados_ei.estado_civil)} />
            <Field label="Profissão" value={values.dados_ei.profissao} />
            <Field label="Data de nascimento" value={values.dados_ei.dt_nascimento} />
            <Field label="Naturalidade" value={values.dados_ei.naturalidade} />
            <Field label="Gênero" value={values.dados_ei.genero} />
            <Field
              label="Endereço de residência"
              value={fmtEnd(values.dados_ei.endereco_estruturado)}
            />
            <Field label="Sede do EI" value={fmtEnd(values.dados_ei.sede_estruturada)} />
          </SimpleGrid>
        </Box>
      )}

      <Box borderWidth="1px" borderRadius="md" p={4} mb={4}>
        <Flex align="center" justify="space-between">
          <Text fontWeight="bold">Empresa</Text>
          {isAlteracao && (
            <Button
              size="xs"
              variant="ghost"
              colorScheme="blue"
              onClick={empresaDisclosure.onToggle}
            >
              {empresaDisclosure.isOpen ? 'Ocultar detalhes' : 'Ver todos os dados'}
            </Button>
          )}
        </Flex>
        {isAlteracao && !empresaDisclosure.isOpen && (
          <Text fontSize="sm" color="gray.600" mt={1}>
            {values.razao_social || '—'} · CNPJ {values.cnpj || '—'}
          </Text>
        )}
        <Collapse in={empresaDisclosure.isOpen} animateOpacity>
          <Box pt={3}>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
              <Field label="Razão social" value={values.razao_social} />
              <Field label="Nome fantasia" value={values.nome_fantasia} />
              {isAlteracao && <Field label="CNPJ" value={values.cnpj} />}
              {isAlteracao && <Field label="Empresa ID" value={values.empresa_id} />}
              <Field label="Capital social" value={fmtBRL(values.capital_social)} />
              <Field
                label="Data início atividades"
                value={fmtData(values.data_inicio_atividades)}
              />
              {/* Foro: constituicao v3 usa estruturado (cidade/UF); alteracao
              usa legacy string livre. Exibe o formato preenchido. */}
              {isAlteracao ? (
                <Field label="Foro" value={values.foro} />
              ) : (
                <Field
                  label="Foro"
                  value={
                    values.foro_cidade || values.foro_uf
                      ? `${values.foro_cidade ?? ''}${values.foro_uf ? ` / ${values.foro_uf}` : ''}`
                      : null
                  }
                />
              )}
              {/* Alteração: a sede nova (tipo marcado) ou o texto do vigente. */}
              {isAlteracao ? (
                <Field
                  label="Endereço da sede"
                  value={sedeNovaEmTexto(values) ?? values.endereco}
                />
              ) : (
                <Field
                  label="Endereço da sede"
                  value={
                    values.endereco_estruturado?.logradouro
                      ? [
                          values.endereco_estruturado.logradouro,
                          values.endereco_estruturado.numero,
                          values.endereco_estruturado.bairro,
                          values.endereco_estruturado.cidade,
                          values.endereco_estruturado.uf,
                          values.endereco_estruturado.cep,
                        ]
                          .filter(Boolean)
                          .join(', ')
                      : null
                  }
                />
              )}
              <Field label="Cidade de assinatura" value={values.cidade_assinatura} />
              {isAlteracao ? (
                <Field label="Estado da Junta Comercial" value={values.junta_comercial_estado} />
              ) : (
                <Field label="UF da Junta Comercial" value={values.junta_comercial_sigla} />
              )}
              {!isAlteracao && (
                <>
                  <Field label="Qtd quotas" value={fmtNum(values.capital_qtd_quotas)} />
                  <Field label="Porte" value={values.porte} />
                  <Field
                    label="Natureza"
                    value={
                      // eslint-disable-next-line no-nested-ternary
                      values.natureza_societaria === 'holding'
                        ? 'Holding'
                        : values.natureza_societaria === 'scp'
                          ? 'SCP'
                          : 'LTDA'
                    }
                  />
                  <Field label="Data assinatura" value={values.data_assinatura} />
                </>
              )}
            </SimpleGrid>

            {isAlteracao && (
              <>
                <Divider my={3} />
                <Text fontSize="sm" fontWeight="bold" color="purple.600" mb={2}>
                  Dados da alteração
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                  {/* "Data da constituição" saiu da alteração (2026-08-06) — ver StepEmpresa. */}
                  <Field label="Sigla Junta Comercial" value={values.junta_comercial_sigla} />
                  <Field label="NIRE" value={values.nire} />
                  <Field
                    label="Data de assinatura"
                    value={
                      values.data_assinatura
                        ? String(values.data_assinatura).slice(0, 10).split('-').reverse().join('/')
                        : 'em branco (preencher à mão)'
                    }
                  />
                </SimpleGrid>
              </>
            )}

            <Divider my={3} />
            <Field
              label={`CNAEs (${values.cnaes?.length ?? 0})`}
              value={
                values.cnaes && values.cnaes.length > 0
                  ? values.cnaes.map(c => `${c.codigo} — ${c.descricao}`).join('; ')
                  : '—'
              }
            />
            {isAlteracao && (
              <Box mt={3}>
                <Field label="Objeto social" value={values.objeto_social} />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>

      <Box borderWidth="1px" borderRadius="md" p={4}>
        <Flex align="center" justify="space-between" mb={3}>
          <Text fontWeight="bold">Sócios ({values.socios.length})</Text>
          {isAlteracao && targetQuotas != null && values.socios.length > 0 && (
            <Badge
              colorScheme={somaConfere ? 'green' : 'red'}
              variant="subtle"
              fontSize="sm"
              px={2}
              py={1}
              borderRadius="md"
            >
              {totalPermanecentes.toLocaleString('pt-BR')} / {targetQuotas.toLocaleString('pt-BR')}{' '}
              quotas {somaConfere ? '✓' : '⚠'}
            </Badge>
          )}
        </Flex>
        <VStack align="stretch" spacing={3}>
          {values.socios.map((s, idx) => {
            const isPJ = s.tipo_pessoa === 'pj';
            const ehSai = s.acao === 'sair';
            const ehNovo = s.acao === 'adicionar';
            // #5: cor simétrica por ação — sai=vermelho, novo=verde, mantém/altera=neutro.
            let cor = 'gray';
            if (ehSai) cor = 'red';
            else if (ehNovo) cor = 'green';
            const acaoInfo = s.acao ? acaoLabels[s.acao] : undefined;
            // Transição de quotas por sócio (antes → depois) pra quem permanece.
            const docS = String(s.cpf || s.cnpj || '').replace(/\D/g, '');
            const quotasAntes = docS ? quotasVigentePorDoc[docS] : undefined;
            const quotasMudou =
              !ehSai &&
              !ehNovo &&
              quotasAntes != null &&
              Number(quotasAntes) !== Number(s.quotas ?? 0);
            let quotasNode: React.ReactNode = fmtNum(s.quotas);
            if (ehSai) {
              quotasNode = `${fmtNum(s.quotas ?? 0)} → transferidas na saída`;
            } else if (ehNovo) {
              quotasNode = `${fmtNum(s.quotas)} (novo)`;
            } else if (quotasMudou) {
              quotasNode = (
                <>
                  <Text as="span" color="gray.500">
                    {fmtNum(quotasAntes)}
                  </Text>
                  <Text as="span" color="green.600" fontWeight="bold" px={1}>
                    →
                  </Text>
                  <Text as="span" fontWeight="bold" color="green.700">
                    {fmtNum(s.quotas)}
                  </Text>
                </>
              );
            }
            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`socio-${idx}`}
                borderWidth="1px"
                borderColor={`${cor}.200`}
                borderRadius="md"
                p={3}
                bg={`${cor}.50`}
                opacity={ehSai ? 0.8 : 1}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Text fontWeight="medium">
                      {idx + 1}. {s.nome || '— (sem nome)'}
                    </Text>
                    <Badge colorScheme={isPJ ? 'purple' : 'green'}>
                      {tipoPessoaLabels[s.tipo_pessoa]}
                    </Badge>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    {acaoInfo && <Badge colorScheme={acaoInfo.color}>{acaoInfo.label}</Badge>}
                    <Badge colorScheme={values.natureza_societaria === 'scp' ? 'purple' : 'blue'}>
                      {(() => {
                        if (values.natureza_societaria !== 'scp') {
                          return tipoVinculoLabels[s.tipo_vinculo];
                        }
                        if (s.papel_scp === 'ostensiva') return 'Sócia Ostensiva';
                        if (s.papel_scp === 'participante') return 'Sócia Participante';
                        return '— (defina o papel)';
                      })()}
                    </Badge>
                  </Box>
                </Box>
                <SimpleGrid columns={{ base: 2, md: 4 }} gap={2}>
                  {isPJ ? (
                    <>
                      <Field label="CNPJ" value={s.cnpj} />
                      {/* Schema-extension v3 elo 3: prioriza sub-objeto
                          `representante` (formato novo); fallback gradual
                          pros flat `representante_nome/cpf` durante transicao. */}
                      <Field
                        label="Representante"
                        value={s.representante?.nome ?? s.representante_nome}
                      />
                      <Field
                        label="CPF do repr."
                        value={s.representante?.cpf ?? s.representante_cpf}
                      />
                      {!isAlteracao && (
                        <>
                          <Field label="UF Junta PJ" value={s.junta_uf} />
                          <Field label="Gênero repr." value={s.representante?.genero} />
                          <Field
                            label="Estado civil repr."
                            value={labelEstadoCivil(s.representante?.estado_civil)}
                          />
                          {(s.representante?.estado_civil === 'casado' ||
                            s.representante?.estado_civil === 'uniao_estavel') && (
                            <Field
                              label="Regime de bens repr."
                              value={labelRegimeCasamento(s.representante?.regime_casamento)}
                            />
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Field label="CPF" value={s.cpf} />
                      <Field label="RG" value={s.rg} />
                      <Field label="Nacionalidade" value={s.nacionalidade} />
                      {/* Quem continua na alteração mantém a do texto do vigente: o campo vem vazio. */}
                      {(!isAlteracao || s.acao === 'adicionar') && (
                        <Field label="Naturalidade" value={s.naturalidade} />
                      )}
                      {!isAlteracao && <Field label="Gênero" value={s.genero} />}
                      <Field label="Estado civil" value={labelEstadoCivil(s.estado_civil)} />
                      {(s.estado_civil === 'casado' || s.estado_civil === 'uniao_estavel') && (
                        <>
                          <Field
                            label="Regime de bens"
                            value={labelRegimeCasamento(s.regime_casamento)}
                          />
                          <Field label="Cônjuge" value={s.nome_conjuge} />
                          <Field label="CPF do cônjuge" value={s.cpf_conjuge} />
                        </>
                      )}
                    </>
                  )}
                  <Field label="Quotas" value={quotasNode} />
                </SimpleGrid>
                {/* v3 constituicao: endereco estruturado do socio. Sem isso
                    o operador nao via que esqueceu de preencher (gate
                    backend rejeitava sem feedback no wizard). */}
                {!isAlteracao && (
                  <Box mt={2}>
                    <Field
                      label={isPJ ? 'Endereço da PJ' : 'Endereço residencial'}
                      value={
                        s.endereco_estruturado?.logradouro
                          ? [
                              s.endereco_estruturado.logradouro,
                              s.endereco_estruturado.numero,
                              s.endereco_estruturado.bairro,
                              s.endereco_estruturado.cidade,
                              s.endereco_estruturado.uf,
                              s.endereco_estruturado.cep,
                            ]
                              .filter(Boolean)
                              .join(', ')
                          : null
                      }
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </VStack>

        {isAlteracao && targetQuotas != null && values.socios.length > 0 && (
          <>
            <Divider my={3} />
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" color="gray.600">
                Total de quotas (pós-alteração)
              </Text>
              <Text fontSize="sm" fontWeight="bold" color={somaConfere ? 'green.600' : 'red.600'}>
                {totalPermanecentes.toLocaleString('pt-BR')} /{' '}
                {targetQuotas.toLocaleString('pt-BR')} {somaConfere ? '✓' : '⚠'}
              </Text>
            </Box>
            {!somaConfere && (
              <Text fontSize="xs" color="red.600" mt={1}>
                A soma das quotas dos sócios que permanecem deve bater com o capital — revise antes
                de gerar.
              </Text>
            )}
          </>
        )}
        {cessoes.length > 0 && (
          <Box mt={3}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.700">
              Cessões declaradas
            </Text>
            {cessoes.map(c => (
              <Text key={c} fontSize="sm" color="gray.700">
                • {c}
              </Text>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StepRevisao;
